/**
 * Toast 通知组件
 * 使用 Headless UI 的 Transition 实现优雅的过渡效果
 */
import React, { Fragment, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  XMarkIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  show: boolean;
  type: ToastType;
  title: string;
  message?: string;
  messages?: string[]; // 支持多条消息
  duration?: number; // 自动关闭时间（毫秒），0表示不自动关闭
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({
  show,
  type,
  title,
  message,
  messages,
  duration = 5000,
  onClose,
}) => {
  // 自动关闭
  useEffect(() => {
    if (show && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration, onClose]);

  // 根据类型选择图标和颜色
  const getIconAndColor = () => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircleIcon,
          bgColor: 'bg-green-50',
          iconColor: 'text-green-400',
          titleColor: 'text-green-800',
          messageColor: 'text-green-700',
          borderColor: 'border-green-200',
        };
      case 'error':
        return {
          icon: ExclamationCircleIcon,
          bgColor: 'bg-red-50',
          iconColor: 'text-red-400',
          titleColor: 'text-red-800',
          messageColor: 'text-red-700',
          borderColor: 'border-red-200',
        };
      case 'warning':
        return {
          icon: ExclamationTriangleIcon,
          bgColor: 'bg-yellow-50',
          iconColor: 'text-yellow-400',
          titleColor: 'text-yellow-800',
          messageColor: 'text-yellow-700',
          borderColor: 'border-yellow-200',
        };
      case 'info':
        return {
          icon: InformationCircleIcon,
          bgColor: 'bg-blue-50',
          iconColor: 'text-blue-400',
          titleColor: 'text-blue-800',
          messageColor: 'text-blue-700',
          borderColor: 'border-blue-200',
        };
    }
  };

  const { icon: Icon, bgColor, iconColor, titleColor, messageColor, borderColor } = getIconAndColor();

  return (
    <div
      aria-live="assertive"
      className="fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start z-[9999]"
    >
      <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
        <Transition
          show={show}
          as={Fragment}
          enter="transform ease-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
          enterTo="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={`max-w-xs w-full ${bgColor} border ${borderColor} shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden`}>
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Icon className={`h-6 w-6 ${iconColor}`} aria-hidden="true" />
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className={`text-sm font-medium ${titleColor}`}>
                    {title}
                  </p>
                  {message && (
                    <p className={`mt-1 text-sm ${messageColor}`}>
                      {message}
                    </p>
                  )}
                  {messages && messages.length > 0 && (
                    <ul className={`mt-2 text-sm ${messageColor} list-disc list-inside space-y-1`}>
                      {messages.map((msg, index) => (
                        <li key={index}>{msg}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    type="button"
                    className={`rounded-md inline-flex ${bgColor} ${titleColor} hover:${messageColor} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-${bgColor.split('-')[1]}-50 focus:ring-${iconColor.split('-')[1]}-500`}
                    onClick={onClose}
                  >
                    <span className="sr-only">关闭</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );
};

export default Toast;
